/**
 * Minimal MCP stdio client for chrome-devtools-mcp.
 *
 * We don't pull in a full MCP SDK — we only need `initialize`,
 * `notifications/initialized`, `tools/list`, and `tools/call`. JSON-RPC 2.0
 * over line-delimited stdout.
 *
 * Version pinned. Bump `CHROME_DEVTOOLS_MCP_VERSION` deliberately and re-run
 * the translator coverage checks whenever the upstream tool shapes change.
 *
 * @module
 */

import { spawn, type ChildProcessWithoutNullStreams } from 'node:child_process';
import { createInterface } from 'node:readline';

export const CHROME_DEVTOOLS_MCP_VERSION = '0.21.0';

const CALL_TIMEOUT_MS = 60_000;
const INITIALIZE_TIMEOUT_MS = 30_000;
const SHUTDOWN_GRACE_MS = 5_000;

export interface McpContent {
  type: string;
  text?: string;
  data?: string;
  mimeType?: string;
}

export interface McpToolResult {
  content?: McpContent[];
  isError?: boolean;
  [k: string]: unknown;
}

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  method: string;
}

export interface McpClientLogger {
  info: (msg: string) => void;
  warn: (msg: string) => void;
  error: (msg: string) => void;
}

export class ChromeDevtoolsMcpClient {
  private child: ChildProcessWithoutNullStreams | null = null;
  private pending = new Map<number, Pending>();
  private nextId = 1;
  private initialized = false;
  private readonly logger: McpClientLogger;

  constructor(logger?: McpClientLogger) {
    this.logger = logger ?? {
      info: (m) => console.log(`[cdp-proxy] ${m}`),
      warn: (m) => console.warn(`[cdp-proxy] ${m}`),
      error: (m) => console.error(`[cdp-proxy] ${m}`),
    };
  }

  async start(): Promise<void> {
    if (this.child) throw new Error('ChromeDevtoolsMcpClient already started');

    const child = spawn(
      'npx',
      ['-y', `chrome-devtools-mcp@${CHROME_DEVTOOLS_MCP_VERSION}`, '--autoConnect'],
      {
        stdio: ['pipe', 'pipe', 'pipe'],
        shell: process.platform === 'win32',
        windowsHide: true,
      },
    );
    this.child = child;

    child.on('exit', (code, signal) => {
      this.logger.info(`chrome-devtools-mcp exited (code=${code} signal=${signal})`);
      for (const [, p] of this.pending) {
        clearTimeout(p.timer);
        p.reject(new Error(`chrome-devtools-mcp subprocess exited before responding to ${p.method}`));
      }
      this.pending.clear();
      this.child = null;
    });

    child.stderr.on('data', (chunk: Buffer) => {
      // chrome-devtools-mcp logs usage/telemetry notices here; keep at debug level
      const line = chunk.toString().trimEnd();
      if (line) this.logger.info(`[cdp-mcp stderr] ${line}`);
    });

    const rl = createInterface({ input: child.stdout });
    rl.on('line', (line) => this.onLine(line));

    await this.request('initialize', {
      protocolVersion: '2024-11-05',
      capabilities: {},
      clientInfo: { name: 'canary-cli', version: '0.0.0' },
    }, INITIALIZE_TIMEOUT_MS);

    this.notify('notifications/initialized', {});
    this.initialized = true;
  }

  async callTool(name: string, args: Record<string, unknown>): Promise<McpToolResult> {
    if (!this.initialized) throw new Error('ChromeDevtoolsMcpClient not initialized');
    const result = await this.request('tools/call', { name, arguments: args });
    return result as McpToolResult;
  }

  async listTools(): Promise<Array<{ name: string; description?: string }>> {
    if (!this.initialized) throw new Error('ChromeDevtoolsMcpClient not initialized');
    const result = (await this.request('tools/list', {})) as { tools?: Array<{ name: string; description?: string }> };
    return result.tools ?? [];
  }

  async shutdown(): Promise<void> {
    const child = this.child;
    if (!child) return;

    try {
      child.stdin.end();
    } catch {
      // ignore
    }

    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      child.once('exit', finish);
      setTimeout(() => {
        if (!done) {
          try {
            child.kill('SIGTERM');
          } catch {
            // ignore
          }
        }
      }, SHUTDOWN_GRACE_MS);
      setTimeout(() => {
        if (!done) {
          try {
            child.kill('SIGKILL');
          } catch {
            // ignore
          }
          finish();
        }
      }, SHUTDOWN_GRACE_MS * 2);
    });

    this.child = null;
    this.initialized = false;
  }

  private request(method: string, params: unknown, timeoutMs: number = CALL_TIMEOUT_MS): Promise<unknown> {
    const child = this.child;
    if (!child) return Promise.reject(new Error('chrome-devtools-mcp subprocess not running'));

    const id = this.nextId++;
    const payload = JSON.stringify({ jsonrpc: '2.0', id, method, params });

    return new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(id)) {
          reject(new Error(`Timeout waiting for ${method} (id=${id}) after ${timeoutMs}ms`));
        }
      }, timeoutMs);

      this.pending.set(id, { resolve, reject, timer, method });

      try {
        child.stdin.write(payload + '\n');
      } catch (err) {
        this.pending.delete(id);
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  private notify(method: string, params: unknown): void {
    const child = this.child;
    if (!child) return;
    try {
      child.stdin.write(JSON.stringify({ jsonrpc: '2.0', method, params }) + '\n');
    } catch (err) {
      this.logger.warn(`notify ${method} failed: ${String(err)}`);
    }
  }

  private onLine(line: string): void {
    const trimmed = line.trim();
    if (!trimmed) return;
    let msg: { id?: number; result?: unknown; error?: { code: number; message: string }; method?: string };
    try {
      msg = JSON.parse(trimmed);
    } catch {
      this.logger.warn(`non-JSON line from chrome-devtools-mcp: ${trimmed.slice(0, 200)}`);
      return;
    }
    if (typeof msg.id === 'number' && this.pending.has(msg.id)) {
      const p = this.pending.get(msg.id)!;
      this.pending.delete(msg.id);
      clearTimeout(p.timer);
      if (msg.error) {
        p.reject(new Error(`${msg.error.code}: ${msg.error.message}`));
      } else {
        p.resolve(msg.result);
      }
    }
    // Server-initiated notifications/requests are ignored for now.
  }
}
